import React, { FC, useEffect, useMemo, useState } from 'react';
import { Secretary, SecretaryCardListProps } from './secretary';
import {
  getAvailableSecretarysCategories,
  getSecretarys,
} from '../../apis/secretary';
import { useScreenSize } from '../../hooks/useScreenSize';

export interface SecretaryCard {
  name: string;
  shortName: string;
  image: string;
  location?: string;
  date?: string;
  phone1: string;
  phone2: string;
  link: string;
  slug: string;
}

export interface Category {
  description: string;
  hidden: boolean;
  icon: string;
  id: string;
  name: string;
  orderIndex: number;
  slug: string;
}

export type CategoryFilter = Pick<Category, 'id' | 'name'>;

export const SecretaryContainer: FC = () => {
  const [allSecretaries, setAllSecretaries] = useState<SecretaryCard[]>([]);
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = useScreenSize();

  const [value, setValue] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryFilter[]
  >([]);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const onClickSearch = () => {
    setAppliedSearch(value);
    setPage(1);
  };

  const onChangeText = (text: string) => {
    setValue(text);
    setAppliedSearch(text);
    setPage(1);
  };

  const handleCheckboxChange = ({ id, name }: CategoryFilter) => {
    setSelectedCategories((prev) => {
      const exists = prev.find((item) => item.id === id);

      if (exists) {
        return prev.filter((item) => item.id !== id);
      }

      return [...prev, { id, name }];
    });
  };

  const handleFilterByCategory = () => {
    setOpen(false);
    onFilterByCategory(selectedCategories);
  };

  const handleChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const onFilterByCategory = (selectedCategories: CategoryFilter[]) => {
    console.log(selectedCategories);
  };

  useEffect(() => {
    getAvailableSecretarysCategories().then((result) => {
      setCategoryList(result);
    });
  }, []);

  useEffect(() => {
    getSecretarys().then((cards) => {
      setAllSecretaries(cards);
    });
  }, []);

  const normalizeString = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filteredSecretaries = useMemo(() => {
    const trimmedSearch = appliedSearch.trim();
    if (!trimmedSearch) {
      return allSecretaries;
    }

    const searchTerm = normalizeString(trimmedSearch);

    return allSecretaries.filter((item) => {
      const name = normalizeString(item.name);
      const shortName = normalizeString(item.shortName);
      return name.includes(searchTerm) || shortName.includes(searchTerm);
    });
  }, [allSecretaries, appliedSearch]);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(filteredSecretaries.length / itemsPerPage);
    return pages > 0 ? pages : 1;
  }, [filteredSecretaries, itemsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedSecretaries = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredSecretaries.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSecretaries, page, itemsPerPage]);

  const secretaryViewProps: SecretaryCardListProps = {
    cards: paginatedSecretaries,
    categories: categoryList,
    totalPages,
    handleChange,
    page,
    toggleDrawer,
    selectedCategories,
    handleCheckboxChange,
    handleFilterByCategory,
    open,
    onClickSearch,
    onChangeText,
    value,
  };

  return <Secretary {...secretaryViewProps} />;
};
